"use client";

/**
 * Tier-2 embedded, theme-matched Stripe checkout.
 *
 * Renders the Stripe Payment Element INSIDE a BB dialog — no redirect to a
 * hosted Stripe page — styled live from the active theme via
 * buildStripeAppearance(). The caller hands us the same body it would POST to
 * /api/checkout; we POST it to /api/payment-intent instead, mount <Elements>
 * with the returned client_secret, and on success call onPaid(paymentIntentId)
 * so the caller can finish the job (e.g. navigate to the verified download).
 */

import { useEffect, useMemo, useState } from "react";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, Lock } from "lucide-react";
import Image from "next/image";
import { getStripe } from "@/services/stripe";
import { buildStripeAppearance } from "@/lib/stripe-appearance";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export interface ThemedPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The JSON body POSTed to /api/payment-intent (type + amount + metadata). */
  body: Record<string, any>;
  amountCents: number;
  currency?: string;
  title: string;
  subtitle?: string;
  coverArtUrl?: string;
  /** Verb on the pay button, e.g. "Pay & download". Amount is appended. */
  payLabel?: string;
  /** Fired after the PaymentIntent succeeds with no redirect. */
  onPaid: (paymentIntentId: string) => void;
}

function formatAmount(cents: number, currency = "eur") {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/** Inner form — must live under <Elements> so the Stripe hooks resolve. */
function CheckoutForm({
  amountCents, currency, payLabel, onPaid,
}: {
  amountCents: number; currency: string; payLabel: string; onPaid: (id: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  const submit = async () => {
    if (!stripe || !elements || busy) return;
    setBusy(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        // No redirect-based methods are enabled, so we stay on-page; a return_url
        // is still required by the API for the rare wallet that needs it.
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (error) {
        toast({ title: "Payment failed", description: error.message || "Please try again.", variant: "destructive" });
        setBusy(false);
        return;
      }
      if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
        onPaid(paymentIntent.id);
        return; // keep the spinner — caller navigates / closes
      }
      // Unexpected non-error, non-success state.
      toast({ title: "Payment not completed", description: `Status: ${paymentIntent?.status || "unknown"}`, variant: "destructive" });
      setBusy(false);
    } catch (e: any) {
      toast({ title: "Payment error", description: e?.message || "Network error.", variant: "destructive" });
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <PaymentElement onReady={() => setReady(true)} options={{ layout: "tabs" }} />
      <Button className="w-full" disabled={!stripe || !ready || busy} onClick={submit}>
        {busy ? (
          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing…</>
        ) : (
          <><Lock className="h-3.5 w-3.5 mr-2" />{payLabel} {formatAmount(amountCents, currency)}</>
        )}
      </Button>
      <p className="text-[11px] text-muted-foreground text-center flex items-center justify-center gap-1">
        <Lock className="h-3 w-3" /> Secured by Stripe · your card never touches our servers
      </p>
    </div>
  );
}

export function ThemedPaymentDialog({
  open, onOpenChange, body, amountCents, currency = "eur",
  title, subtitle, coverArtUrl, payLabel = "Pay", onPaid,
}: ThemedPaymentDialogProps) {
  const { toast } = useToast();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fallingBack, setFallingBack] = useState(false);

  const stripePromise = useMemo(() => getStripe(), []);

  // Safety net: if the embedded element can't load (e.g. js.stripe.com blocked
  // by an extension) the buyer can still pay via the hosted Checkout Session.
  // The body we were handed is shape-compatible with /api/checkout.
  const continueClassic = async () => {
    setFallingBack(true);
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, amountCents, origin: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; return; }
      toast({ title: "Could not start checkout", description: data.error || "Try again.", variant: "destructive" });
    } catch (e: any) {
      toast({ title: "Could not start checkout", description: e?.message || "Network error.", variant: "destructive" });
    } finally {
      setFallingBack(false);
    }
  };
  // Snapshot the live theme → Stripe appearance when the secret arrives.
  const appearance = useMemo(() => (clientSecret ? buildStripeAppearance() : undefined), [clientSecret]);

  // Create the PaymentIntent when the dialog opens.
  useEffect(() => {
    if (!open) { setClientSecret(null); setError(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await fetch("/api/payment-intent", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...body, amountCents }),
        });
        const data = await res.json();
        if (cancelled) return;
        if (data.clientSecret) setClientSecret(data.clientSecret);
        else setError(data.error || "Could not start checkout.");
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Network error.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Re-create only when the dialog (re)opens or the amount changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, amountCents]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] max-sm:fixed max-sm:inset-x-0 max-sm:top-[50px] max-sm:bottom-0 max-sm:!translate-x-0 max-sm:!translate-y-0 max-sm:!left-0 max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:overflow-y-auto max-sm:h-auto max-sm:pb-[calc(84px+env(safe-area-inset-bottom))]">
        <DialogHeader>
          <DialogTitle className="line-clamp-2">{title}</DialogTitle>
        </DialogHeader>

        {/* Item summary */}
        <div className="flex items-center gap-3 pb-3 border-b border-border/50">
          {coverArtUrl && (
            <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-muted">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <Image src={coverArtUrl} alt={title} width={48} height={48} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
            <p className="font-semibold text-sm">{formatAmount(amountCents, currency)}</p>
          </div>
        </div>

        <div className={cn("min-h-[120px]")}>
          {loading && (
            <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-xs">Preparing secure checkout…</span>
            </div>
          )}
          {error && !loading && (
            <div className="py-6 text-center space-y-3">
              <p className="text-sm text-destructive">{error}</p>
              <Button size="sm" disabled={fallingBack} onClick={continueClassic}>
                {fallingBack ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Continue to secure checkout
              </Button>
            </div>
          )}
          {clientSecret && !loading && appearance && (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
              <CheckoutForm amountCents={amountCents} currency={currency} payLabel={payLabel} onPaid={onPaid} />
            </Elements>
          )}
        </div>

        {/* Always-available fallback to the hosted page if the embedded form
            misbehaves (blocked Stripe.js, etc.). */}
        {!loading && (
          <button
            type="button"
            onClick={continueClassic}
            disabled={fallingBack}
            className="mt-1 w-full text-center text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50"
          >
            Having trouble? Use classic checkout
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
